import { BridgeClient } from 'app/service/bridge/BridgeClient';

const MODES = new Set(['automatic', 'scheduled', 'paused']);
const RUNTIME_STATES = new Set([
  'automatic_active',
  'waiting_for_schedule',
  'paused',
  'waiting_for_worker',
  'processing',
  'catching_up',
  'failed',
  'unavailable',
]);
const COMPLETION_PHASES = new Set([
  'completion_admission',
  'feed_coverage',
  'runtime_finalize',
  'completion_adapter',
  'terminal_ack',
]);
const COMPLETION_REASONS = new Set([
  'job_owner_conflict',
  'feed_owner_conflict',
  'runtime_state_unavailable',
  'runtime_identity_invalid',
  'coverage_persist_failed',
  'bookkeeping_persist_failed',
  'adapter_failed',
  'job_terminal_persist_failed',
]);
const COMPLETION_CLASSIFICATIONS = new Set(['transient', 'permanent']);
const JOB_ID_PATTERN = /^[A-Za-z0-9_.:-]{1,64}(?![\s\S])/;
const COMPLETION_VERSION_PATTERN = /^[a-f0-9]{64}(?![\s\S])/;
const UTC_TIMESTAMP_PATTERN = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2})(?:\.\d{6})?(?![\s\S])/;
const TRUSTED_ERROR_CODES = new Set([
  'INVALID_PAYLOAD',
  'MODE_UNAVAILABLE',
  'SCHEDULE_UNAVAILABLE',
  'REVISION_CONFLICT',
  'STATUS_UNAVAILABLE',
  'UPDATE_FAILED',
  'RECOVERY_UNAVAILABLE',
  'RECOVERY_FAILED',
]);

function isRecord(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function invalidResponseError() {
  const error = new Error('The plugin returned an invalid catalog update policy.');
  error.code = 'INVALID_CATALOG_UPDATE_POLICY';
  return error;
}

function invalidCompletionRequestError() {
  const error = new Error('The catalog completion recovery request is invalid.');
  error.code = 'INVALID_PAYLOAD';
  return error;
}

function isNullableUtcTimestamp(value) {
  if (value === null) return true;
  if (typeof value !== 'string') return false;
  const match = UTC_TIMESTAMP_PATTERN.exec(value);
  if (!match) return false;

  const [, year, month, day, hour, minute, second] = match.map(Number);
  const date = new Date(Date.UTC(year, month - 1, day, hour, minute, second));
  return date.getUTCFullYear() === year
    && date.getUTCMonth() === month - 1
    && date.getUTCDate() === day
    && date.getUTCHours() === hour
    && date.getUTCMinutes() === minute
    && date.getUTCSeconds() === second;
}

function completionRuntimeIsValid(runtime) {
  if (typeof runtime.completion_status_available !== 'boolean') return false;
  const unavailable = runtime.completion_status_available === false;
  if (unavailable) {
    return runtime.active_index_present === null
      && runtime.live_generation === null
      && runtime.completion_attention_required === false
      && runtime.completion_job_id === null
      && runtime.completion_phase === null
      && runtime.completion_reason === null
      && runtime.completion_classification === null
      && runtime.completion_failure_attempts === 0
      && runtime.completion_retry_at === null
      && runtime.completion_stopped === false
      && runtime.completion_version === null
      && runtime.completion_retry_allowed === false
      && runtime.completion_stop_allowed === false
      && runtime.job_started_at === null
      && runtime.job_last_attempt_at === null
      && runtime.job_last_progress_at === null;
  }

  if (
    typeof runtime.active_index_present !== 'boolean'
    || ![null, 'a', 'b'].includes(runtime.live_generation)
    || runtime.active_index_present !== (runtime.live_generation !== null)
    || typeof runtime.completion_attention_required !== 'boolean'
    || !Number.isInteger(runtime.completion_failure_attempts)
    || runtime.completion_failure_attempts < 0
    || !isNullableUtcTimestamp(runtime.completion_retry_at)
    || typeof runtime.completion_stopped !== 'boolean'
    || (runtime.completion_version !== null
      && (typeof runtime.completion_version !== 'string'
        || !COMPLETION_VERSION_PATTERN.test(runtime.completion_version)))
    || typeof runtime.completion_retry_allowed !== 'boolean'
    || typeof runtime.completion_stop_allowed !== 'boolean'
    || !isNullableUtcTimestamp(runtime.job_started_at)
    || !isNullableUtcTimestamp(runtime.job_last_attempt_at)
    || !isNullableUtcTimestamp(runtime.job_last_progress_at)
  ) return false;

  if (!runtime.completion_attention_required) {
    return runtime.completion_job_id === null
      && runtime.completion_phase === null
      && runtime.completion_reason === null
      && runtime.completion_classification === null
      && runtime.completion_failure_attempts === 0
      && runtime.completion_retry_at === null
      && runtime.completion_stopped === false
      && runtime.completion_version === null
      && runtime.completion_retry_allowed === false
      && runtime.completion_stop_allowed === false
      && runtime.job_started_at === null
      && runtime.job_last_attempt_at === null
      && runtime.job_last_progress_at === null;
  }

  return typeof runtime.completion_job_id === 'string'
    && JOB_ID_PATTERN.test(runtime.completion_job_id)
    && COMPLETION_PHASES.has(runtime.completion_phase)
    && COMPLETION_REASONS.has(runtime.completion_reason)
    && COMPLETION_CLASSIFICATIONS.has(runtime.completion_classification)
    && (!runtime.completion_retry_allowed || runtime.completion_version !== null)
    && (!runtime.completion_stop_allowed || (
      runtime.completion_version !== null
      && runtime.completion_stopped === false
    ));
}

function completionPayload(jobId, completionVersion) {
  if (
    typeof jobId !== 'string'
    || !JOB_ID_PATTERN.test(jobId)
    || typeof completionVersion !== 'string'
    || !COMPLETION_VERSION_PATTERN.test(completionVersion)
  ) throw invalidCompletionRequestError();

  return { job_id: jobId, completion_version: completionVersion };
}

function unwrapBridgeResponse(response) {
  if (!isRecord(response) || !Object.prototype.hasOwnProperty.call(response, 'success')) {
    return response;
  }
  if (response.success !== true) throw invalidResponseError();
  const { success, ...payload } = response;
  return payload;
}

export function normalizeCatalogUpdatePolicy(rawResponse) {
  const response = unwrapBridgeResponse(rawResponse);
  if (isRecord(response) && response.result === 'error') {
    const error = new Error('Catalog update policy request failed.');
    error.code = TRUSTED_ERROR_CODES.has(response.code) ? response.code : 'REQUEST_FAILED';
    throw error;
  }
  const policy = response?.catalog_updates;
  const runtime = policy?.runtime;
  const configuredMode = policy?.configured_mode ?? policy?.mode;
  const effectiveMode = policy?.effective_mode ?? policy?.mode;
  const schedule = policy?.schedule;
  const completionRecoverySupported = policy?.completion_recovery_supported === true;
  if (
    response?.result !== 'ok'
    || !isRecord(policy)
    || !MODES.has(policy.mode)
    || !MODES.has(configuredMode)
    || !MODES.has(effectiveMode)
    || policy.mode !== configuredMode
    || !Number.isInteger(policy.revision)
    || policy.revision < 0
    || (policy.updated_at !== null && typeof policy.updated_at !== 'string')
    || typeof policy.automatic_supported !== 'boolean'
    || (policy.completion_recovery_supported !== undefined
      && typeof policy.completion_recovery_supported !== 'boolean')
    || !Array.isArray(policy.supported_modes)
    || policy.supported_modes.length < 1
    || policy.supported_modes.some(mode => !MODES.has(mode))
    || (!policy.supported_modes.includes(configuredMode)
      && !(configuredMode === 'automatic' && policy.automatic_supported === false))
    || !policy.supported_modes.includes(effectiveMode)
    || policy.automatic_supported !== policy.supported_modes.includes('automatic')
    || (schedule !== null && schedule !== undefined && (
      !isRecord(schedule)
      || !Number.isInteger(schedule.interval_minutes)
      || schedule.interval_minutes < 60
      || schedule.interval_minutes > 43200
      || !Number.isInteger(schedule.window_start_minute_utc)
      || schedule.window_start_minute_utc < 0
      || schedule.window_start_minute_utc >= 1440
      || !Number.isInteger(schedule.window_duration_minutes)
      || schedule.window_duration_minutes < 15
      || schedule.window_duration_minutes > 1440
    ))
    || (runtime !== null && runtime !== undefined && (
      !isRecord(runtime)
      || !RUNTIME_STATES.has(runtime.state)
      || typeof runtime.scheduler_healthy !== 'boolean'
      || typeof runtime.reconciliation_required !== 'boolean'
      || (runtime.manual_recovery_required !== undefined
        && typeof runtime.manual_recovery_required !== 'boolean')
      || !Number.isInteger(runtime.pending_count)
      || runtime.pending_count < 0
      || !Number.isInteger(runtime.failed_count)
      || runtime.failed_count < 0
      || (runtime.pending_count_capped !== undefined && typeof runtime.pending_count_capped !== 'boolean')
      || (runtime.failed_count_capped !== undefined && typeof runtime.failed_count_capped !== 'boolean')
      || [
        'last_scheduler_tick_at',
        'last_scheduler_transport',
        'last_incremental_success_at',
        'last_reconciliation_success_at',
        'next_scheduled_due_at',
        'last_failure_code',
        'oldest_pending_at',
        'active_job_id',
      ].some(key => runtime[key] !== null && typeof runtime[key] !== 'string')
    ))
    || (completionRecoverySupported && (
      !isRecord(runtime)
      || !completionRuntimeIsValid(runtime)
    ))
  ) {
    throw invalidResponseError();
  }

  return {
    mode: policy.mode,
    configuredMode,
    effectiveMode,
    revision: policy.revision,
    updatedAt: policy.updated_at,
    automaticSupported: policy.automatic_supported,
    completionRecoverySupported,
    supportedModes: [...policy.supported_modes],
    schedule: schedule ? {
      intervalMinutes: schedule.interval_minutes,
      windowStartMinuteUtc: schedule.window_start_minute_utc,
      windowDurationMinutes: schedule.window_duration_minutes,
    } : null,
    runtime: runtime ? {
      state: runtime.state,
      schedulerHealthy: runtime.scheduler_healthy,
      lastSchedulerTickAt: runtime.last_scheduler_tick_at,
      lastSchedulerTransport: runtime.last_scheduler_transport,
      lastIncrementalSuccessAt: runtime.last_incremental_success_at,
      lastReconciliationSuccessAt: runtime.last_reconciliation_success_at,
      nextScheduledDueAt: runtime.next_scheduled_due_at,
      lastFailureCode: runtime.last_failure_code,
      reconciliationRequired: runtime.reconciliation_required,
      manualRecoveryRequired: runtime.manual_recovery_required === true,
      pendingCount: runtime.pending_count,
      failedCount: runtime.failed_count,
      pendingCountCapped: runtime.pending_count_capped === true,
      failedCountCapped: runtime.failed_count_capped === true,
      oldestPendingAt: runtime.oldest_pending_at,
      activeJobId: runtime.active_job_id,
      ...(completionRecoverySupported ? {
        completionStatusAvailable: runtime.completion_status_available,
        activeIndexPresent: runtime.active_index_present,
        liveGeneration: runtime.live_generation,
        completionAttentionRequired: runtime.completion_attention_required,
        completionJobId: runtime.completion_job_id,
        completionPhase: runtime.completion_phase,
        completionReason: runtime.completion_reason,
        completionClassification: runtime.completion_classification,
        completionFailureAttempts: runtime.completion_failure_attempts,
        completionRetryAt: runtime.completion_retry_at,
        completionStopped: runtime.completion_stopped,
        completionVersion: runtime.completion_version,
        completionRetryAllowed: runtime.completion_retry_allowed,
        completionStopAllowed: runtime.completion_stop_allowed,
        jobStartedAt: runtime.job_started_at,
        jobLastAttemptAt: runtime.job_last_attempt_at,
        jobLastProgressAt: runtime.job_last_progress_at,
      } : {}),
    } : null,
  };
}

export function createBridgeCatalogUpdatePolicyApi({ client = new BridgeClient() } = {}) {
  const request = async (action, payload) => normalizeCatalogUpdatePolicy(
    await client.sendMessageWithResponse(action, payload),
  );

  return {
    getStatus: () => request('catalog_updates.status', {}),
    setMode: (mode, expectedRevision) => request('catalog_updates.set_mode', {
      mode,
      expected_revision: expectedRevision,
    }),
    setSchedule: (schedule, expectedRevision) => request('catalog_updates.set_schedule', {
      interval_minutes: schedule.intervalMinutes,
      window_start_minute_utc: schedule.windowStartMinuteUtc,
      window_duration_minutes: schedule.windowDurationMinutes,
      expected_revision: expectedRevision,
    }),
    retryCompletion: (jobId, completionVersion) => request(
      'catalog_updates.completion_retry',
      completionPayload(jobId, completionVersion),
    ),
    stopCompletion: (jobId, completionVersion) => request(
      'catalog_updates.completion_stop',
      completionPayload(jobId, completionVersion),
    ),
  };
}
